import React from 'react'
import PropTypes from 'prop-types'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownItem from 'react-bootstrap/DropdownItem'
import { ReactComponent as Building } from '../../assets/svgs/building.svg'
import { ReactComponent as Briefcase } from '../../assets/svgs/briefcase.svg'
import apiCompanies from '../../features/configuration/services/company'
import apiCompanyUsers from '../../features/configuration/services/companyUser'
import { actions, userContext } from '../../contexts/userContext'
import {
  ActiveDropdownItem,
  DropdownToggleBtn,
  StyledDropdownMenu,
  CompanyLogo,
  EllipsisCompanyName,
} from './dropdown'
import cookiesToObject, {
  addCookie,
  clearAgencyTypeCache,
} from '../../utils/cookiesHandler'
import useReloadOnStorageChange from '../../hooks/useReloadOnStorageChange'
import CompanyTransitionModal from '../../features/organizations/components/CompanyTransitionModal'
import useAgencyType from '../../hooks/useAgencyType'
import { useNavigate } from 'react-router-dom'
import { convertKeysToSnakeCase } from '../../utils/stringUtilities'
import { usePermissionsStore } from '../../services/permissionsStore'

function CompanyItem({
  name = '',
  logo = null,
  isAgency = false,
  isInToggle = false,
}) {
  const { agencyId } = cookiesToObject(document.cookie)
  const { agAbbr, orgAbbr } = useAgencyType(agencyId)

  let logoElement
  if (logo) {
    logoElement = <CompanyLogo src={logo} alt={name} />
  } else if (isAgency) {
    logoElement = (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ width: '30px', height: '30px' }}
      >
        <Briefcase style={{ width: '18px', height: '18px' }} />
      </div>
    )
  } else {
    logoElement = (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ width: '30px', height: '30px' }}
      >
        <Building style={{ width: '18px', height: '18px' }} />
      </div>
    )
  }

  const getPrefix = () => {
    if (isAgency && agencyId) {
      return `${agAbbr}  -  `
    }
    if (agencyId && !isAgency) {
      return `${orgAbbr}  -  `
    }
    return ''
  }

  const componentClass = isInToggle
    ? 'toggle-company-item'
    : 'menu-company-item'

  return (
    <div className={`d-flex align-items-center ${componentClass}`}>
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ width: '32px', height: '32px' }}
      >
        {logoElement}
      </div>
      <div className="mx-2 my-0">
        <p className="m-0">{getPrefix()}</p>
      </div>
      <EllipsisCompanyName>{name}</EllipsisCompanyName>
    </div>
  )
}

CompanyItem.propTypes = {
  name: PropTypes.string,
  logo: PropTypes.string,
  isAgency: PropTypes.bool,
}

export default function UserCompanyDropdown() {
  useReloadOnStorageChange('talentinoCompanyId')
  const navigate = useNavigate()
  const { agencyId } = cookiesToObject(document.cookie)
  const { agAbbr, orgAbbr } = useAgencyType(agencyId)
  const user = React.useContext(userContext)
  const [selectedComp, setSelectedComp] = React.useState(-1)
  const [menu, setMenu] = React.useState(false)
  const [companies, setCompanies] = React.useState([])

  const changeCompanyAction = async (comp) => {
    const currentCompany = JSON.parse(
      localStorage.getItem('currentCompany') || '{}',
    )
    localStorage.setItem('previousCompany', JSON.stringify(currentCompany))

    const newCompany = {
      id: companies[comp].id,
      name: companies[comp].companyName,
      type: companies[comp].isAgency ? agAbbr : orgAbbr,
    }
    localStorage.setItem('currentCompany', JSON.stringify(newCompany))

    sessionStorage.setItem('companyChanged', 'true')

    try {
      await apiCompanies.authorize({
        companyId: companies[comp].id,
      })
      localStorage.setItem('talentinoCompanyId', companies[comp].id)
      addCookie('companyId', companies[comp].id)
      addCookie('companyName', companies[comp].companyName)
      addCookie('contactEmail', companies[comp].contactEmail)
      addCookie('title', companies[comp].userTitle)
      addCookie('isAgency', companies[comp].isAgency)
      addCookie('agencyId', companies[comp].agencyId)
      addCookie('roleType', companies[comp].roleType)
      addCookie('permissions', companies[comp].permissions)
      getReadablePermissions()
      user.dispatch({
        type: actions.UPDATE_COMPANY,
        payload: {
          companyId: companies[comp].id,
          companyName: companies[comp].companyName,
          isAgency: companies[comp].isAgency,
          userTitle: convertKeysToSnakeCase(companies[comp].userTitle),
          companyEmail: companies[comp].contactEmail,
          roleType: companies[comp].roleType,
          isActive: companies[comp].isActive,
        },
      })
      clearAgencyTypeCache()
      setMenu(false)
      navigate('/')
    } catch (error) {
      console.error('Company change failed:', error)
    }
  }
  const getReadablePermissions = () => {
    const store = usePermissionsStore.getState()
    store.resetPermissions()
    store.ensureLoaded()
  }

  const toggle = () => setMenu((prev) => !prev)
  const getCompany = (value) =>
    selectedComp !== -1 ? companies[selectedComp][value] : undefined

  const getMenuCompany = (value, index) =>
    companies[index] ? companies[index][value] : undefined

  React.useEffect(() => {
    const fetchCompanies = async () => {
      const response = await apiCompanyUsers.getAll({
        params: { key: 'companies' },
      })
      setCompanies(
        response?.map((result) => ({
          ...result.company,
          userTitle: result.userTitle,
          roleType: result?.role?.roleType,
          permissions: convertKeysToSnakeCase(result?.role?.permissionsMapping),
        })),
      )
    }
    fetchCompanies()
  }, [user.state?.companyId])

  React.useEffect(() => {
    if (user.state?.companyId) {
      const index = companies.findIndex((c) => c.id === user.state.companyId)
      if (index !== -1) setSelectedComp(index)
    }
  }, [user.state?.companyId, companies])

  React.useEffect(() => {
    if (user.state) {
      const i = companies.findIndex((comp) => comp.id === user.state.companyId)
      if (i !== -1) {
        setSelectedComp(i)
        if (!localStorage.getItem('currentCompany')) {
          const currentCompany = {
            id: companies[i].id,
            name: companies[i].companyName,
            type: companies[i].isAgency ? 'AG' : 'ORG',
          }
          localStorage.setItem('currentCompany', JSON.stringify(currentCompany))
        }
      }
    }
  }, [companies])

  const preventRightClick = (event) => {
    event.preventDefault()
  }

  if (companies.length) {
    return (
      <>
        <CompanyTransitionModal key={user.state.companyId} />
        <Dropdown
          show={menu}
          onToggle={toggle}
          className="d-inline-block"
          style={{
            backgroundColor: '#F5F5F5',
            borderRadius: '40px',
            paddingRight: '10px',
          }}
        >
          <DropdownToggleBtn
            className="btn d-flex align-items-center"
            disabled={selectedComp === -1}
          >
            <CompanyItem
              name={getCompany('companyName')}
              logo={getCompany('logo')}
              isAgency={getCompany('isAgency')}
              isInToggle={true}
            />
          </DropdownToggleBtn>
          <StyledDropdownMenu style={{ marginTop: '4px' }}>
            {companies.map((company, index) => {
              const isFirstAndSingle = index === 0 && companies.length === 1
              const isSelected = selectedComp === index
              let borderRadiusStyle = ''

              if (isFirstAndSingle) {
                borderRadiusStyle = '5px'
              } else if (index === 0) {
                borderRadiusStyle = '5px 5px 0 0'
              } else if (index === companies.length - 1) {
                borderRadiusStyle = '0 0 5px 5px'
              }

              const dropdownItemProps = {
                style: {
                  borderRadius: borderRadiusStyle,
                },
                onClick: () => changeCompanyAction(index),
                onContextMenu: preventRightClick,
              }

              const DropdownItemType = isSelected
                ? ActiveDropdownItem
                : DropdownItem

              return (
                <div key={`${company.companyName}-${index}`}>
                  <DropdownItemType {...dropdownItemProps}>
                    <CompanyItem
                      name={company.companyName}
                      logo={getMenuCompany('logo', index)}
                      isAgency={company.isAgency}
                      isInToggle={false}
                    />
                  </DropdownItemType>
                </div>
              )
            })}
          </StyledDropdownMenu>
        </Dropdown>
      </>
    )
  }
  return <div />
}
